import { useCallback, useEffect } from 'react';
import { useAsync } from 'react-use';
import TextField from '@material-ui/core/TextField';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import { CATALOG_FILTER_EXISTS } from '@backstage/catalog-client';
import { parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  EntityDisplayName,
  entityPresentationApiRef,
} from '@backstage/plugin-catalog-react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';

/**
 * Custom EntityPicker that sorts entities alphabetically by their display name.
 * This component extends the standard EntityPicker with sorting functionality.
 */
export const SortedEntityPickerComponent = (
  props: FieldExtensionComponentProps<string>,
) => {
  const {
    onChange,
    schema: { title = 'Entity', description = 'Select an entity' },
    required,
    uiSchema,
    rawErrors,
    formData,
    idSchema,
  } = props;

  const catalogFilter = buildCatalogFilter(uiSchema);
  const defaultKind =
    typeof uiSchema['ui:options']?.defaultKind === 'string'
      ? uiSchema['ui:options']?.defaultKind
      : undefined;
  const defaultNamespace =
    typeof uiSchema['ui:options']?.defaultNamespace === 'string'
      ? uiSchema['ui:options']?.defaultNamespace
      : undefined;
  const isDisabled = uiSchema?.['ui:disabled'] ?? false;

  const catalogApi = useApi(catalogApiRef);
  const entityPresentationApi = useApi(entityPresentationApiRef);

  const { value: entities, loading } = useAsync(async () => {
    const fields = [
      'kind',
      'metadata.name',
      'metadata.namespace',
      'metadata.title',
      'metadata.description',
      'spec.profile.displayName',
      'spec.type',
    ];

    const { items } = await catalogApi.getEntities(
      catalogFilter ? { filter: catalogFilter, fields } : { filter: undefined, fields }
    );

    const entityRefToPresentation = new Map(
      await Promise.all(
        items.map(async item => {
          const presentation = await entityPresentationApi.forEntity(item).promise;
          return [stringifyEntityRef(item), presentation] as const;
        }),
      ),
    );

    // Sort entities alphabetically by their primary title
    const sortedItems = [...items].sort((a, b) => {
      const aPresentation = entityRefToPresentation.get(stringifyEntityRef(a));
      const bPresentation = entityRefToPresentation.get(stringifyEntityRef(b));
      const aTitle = aPresentation?.primaryTitle || stringifyEntityRef(a);
      const bTitle = bPresentation?.primaryTitle || stringifyEntityRef(b);
      return aTitle.localeCompare(bTitle);
    });

    return { catalogEntities: sortedItems, entityRefToPresentation };
  });

  const allowArbitraryValues = Boolean(
    uiSchema['ui:options']?.allowArbitraryValues ?? true,
  );

  const getLabel = useCallback(
    (freeSoloValue: string) => {
      try {
        const parsedRef = parseEntityRef(freeSoloValue, {
          defaultKind,
          defaultNamespace,
        });
        return stringifyEntityRef(parsedRef);
      } catch (err) {
        return freeSoloValue;
      }
    },
    [defaultKind, defaultNamespace],
  );

  const onSelect = useCallback(
    (_: any, ref: any, reason: any) => {
      if (typeof ref !== 'string') {
        onChange(ref ? stringifyEntityRef(ref) : undefined);
      } else {
        if (reason === 'blur' || reason === 'create-option') {
          let entityRef = ref;
          try {
            entityRef = stringifyEntityRef(
              parseEntityRef(ref, {
                defaultKind,
                defaultNamespace,
              }),
            );
          } catch (err) {
            // Ignore parse errors
          }
          if (formData !== ref || allowArbitraryValues) {
            onChange(entityRef);
          }
        }
      }
    },
    [onChange, formData, defaultKind, defaultNamespace, allowArbitraryValues],
  );

  const selectedEntity =
    entities?.catalogEntities.find(e => stringifyEntityRef(e) === formData) ??
    (allowArbitraryValues && formData ? getLabel(formData) : '');

  useEffect(() => {
    if (
      required &&
      !allowArbitraryValues &&
      entities?.catalogEntities.length === 1 &&
      selectedEntity === ''
    ) {
      onChange(stringifyEntityRef(entities.catalogEntities[0]));
    }
  }, [entities, onChange, selectedEntity, required, allowArbitraryValues]);

  return (
    <Autocomplete
      disabled={
        isDisabled ||
        (required &&
          !allowArbitraryValues &&
          entities?.catalogEntities.length === 1)
      }
      id={idSchema?.$id}
      value={selectedEntity}
      loading={loading}
      onChange={onSelect}
      options={entities?.catalogEntities || []}
      getOptionLabel={option =>
        typeof option === 'string'
          ? option
          : entities?.entityRefToPresentation.get(stringifyEntityRef(option))
              ?.entityRef || ''
      }
      autoSelect
      freeSolo={allowArbitraryValues}
      renderInput={params => (
        <TextField
          {...params}
          label={title}
          margin="dense"
          helperText={description}
          variant="outlined"
          required={required}
          disabled={isDisabled}
          InputProps={params.InputProps}
          error={rawErrors && rawErrors.length > 0}
        />
      )}
      renderOption={option => <EntityDisplayName entityRef={option} />}
      filterOptions={createFilterOptions({
        stringify: option =>
          entities?.entityRefToPresentation.get(stringifyEntityRef(option))
            ?.primaryTitle || '',
      })}
    />
  );
};

function convertOpsValues(value: any): string | undefined {
  if (typeof value === 'object' && value.exists) {
    return CATALOG_FILTER_EXISTS as unknown as string;
  }
  return value?.toString();
}

function convertSchemaFiltersToQuery(schemaFilters: Record<string, any>) {
  const query: Record<string, any> = {};
  for (const [key, value] of Object.entries(schemaFilters)) {
    if (Array.isArray(value)) {
      query[key] = value;
    } else {
      query[key] = convertOpsValues(value);
    }
  }
  return query;
}

function buildCatalogFilter(uiSchema: any) {
  const allowedKinds = uiSchema['ui:options']?.allowedKinds;
  const catalogFilter =
    uiSchema['ui:options']?.catalogFilter ||
    (allowedKinds && { kind: allowedKinds });

  if (!catalogFilter) {
    return undefined;
  }

  if (Array.isArray(catalogFilter)) {
    return catalogFilter.map(convertSchemaFiltersToQuery);
  }

  return convertSchemaFiltersToQuery(catalogFilter);
}

/**
 * Export the field extension for use in the scaffolder.
 * Use this in templates with: ui:field: SortedEntityPicker
 */
export const SortedEntityPickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'SortedEntityPicker',
    component: SortedEntityPickerComponent,
  }),
);
